import { create } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';

export type SuppliersExhibitionType =
    | 'PRODUCT_SUPPLIERS_CLUSTER'
    | 'PRODUCT_SUPPLIERS_STORE'
    | 'PRODUCT_SUPPLIERS_NETWORK';

export type GroupByType = 'CLUSTER' | 'STORE' | 'NETWORK';

export type ProductType = 'PRODUCT';

type ExhibitionCombinationKey = `${GroupByType}_${ProductType}`;

interface ExhibitionGrouping {
    groupBy: GroupByType;
    productType: ProductType;
}

interface SuppliersExhibitionStore {
    suppliersExhibitionType: SuppliersExhibitionType;
    setSuppliersExhibitionType: (type: SuppliersExhibitionType) => void;
    updateExhibitionType: (groupBy: GroupByType, productType: ProductType) => void;
}

// Este mapa é parcial de propósito: nem toda combinação de agrupamento e tipo de produto que dá
// para escrever tem tradução aqui, e um tipo de produto novo entra sem nenhuma. Declará-lo total
// fazia o verificador de tipos tratar a guarda lá embaixo como código morto, e aí um erro de
// digitação de quem chama virava uma troca de agrupamento que silenciosamente não acontecia.
const EXHIBITION_TYPE_MAP: Partial<Record<ExhibitionCombinationKey, SuppliersExhibitionType>> = {
    'CLUSTER_PRODUCT': 'PRODUCT_SUPPLIERS_CLUSTER',
    'STORE_PRODUCT': 'PRODUCT_SUPPLIERS_STORE',
    'NETWORK_PRODUCT': 'PRODUCT_SUPPLIERS_NETWORK',
};

/** O tipo de exibição de um agrupamento, ou nada se a combinação não existe. */
export const exhibitionTypeOf = (groupBy: GroupByType, productType: ProductType): SuppliersExhibitionType | undefined =>
    EXHIBITION_TYPE_MAP[`${groupBy}_${productType}`];

// O agrupamento em que a tela abre.
const OPENING_EXHIBITION_GROUPING: ExhibitionGrouping = { groupBy: 'NETWORK', productType: 'PRODUCT' };

// O mapa inverso também é parcial na leitura, e por um motivo de tempo de execução: a função que
// grava o tipo de exibição é exportada sem validação nenhuma, então um valor fora da união chega ao
// estado sem nada no meio e cai fora deste mapa. A cláusula de satisfação continua exigindo entrada
// para cada valor da união — parcial para quem lê, completo para quem escreve.
const REVERSE_EXHIBITION_TYPE_MAP: Partial<Record<SuppliersExhibitionType, ExhibitionGrouping>> = {
    'PRODUCT_SUPPLIERS_CLUSTER': { groupBy: 'CLUSTER', productType: 'PRODUCT' },
    'PRODUCT_SUPPLIERS_STORE': { groupBy: 'STORE', productType: 'PRODUCT' },
    'PRODUCT_SUPPLIERS_NETWORK': OPENING_EXHIBITION_GROUPING,
} satisfies Record<SuppliersExhibitionType, ExhibitionGrouping>;

// Recua para o agrupamento de abertura em vez de ler campo de indefinido: o seletor de agrupamento
// é o único caminho para a visão rede, e um estouro aqui não degrada um pedaço da tela, derruba o
// cabeçalho inteiro.
const resolveExhibitionGrouping = (type: SuppliersExhibitionType): ExhibitionGrouping =>
    REVERSE_EXHIBITION_TYPE_MAP[type] ?? OPENING_EXHIBITION_GROUPING;

// A escolha de agrupamento da tela geral fica guardada neste navegador.
export const SUPPLIERS_EXHIBITION_STORAGE_KEY = 'ipa.negociacao-fornecedor.agrupamento';

// Sem escolha guardada (ou com uma desconhecida ou ilegível), a tela abre agrupada por toda a rede.
const OPENING_EXHIBITION_TYPE: SuppliersExhibitionType = 'PRODUCT_SUPPLIERS_NETWORK';

const isSuppliersExhibitionType = (value: unknown): value is SuppliersExhibitionType =>
    typeof value === 'string' && Object.prototype.hasOwnProperty.call(REVERSE_EXHIBITION_TYPE_MAP, value);

type PersistedExhibition = Pick<SuppliersExhibitionStore, 'suppliersExhibitionType'>;

// Guardar a escolha é conveniência: navegador sem localStorage, ou com a cota cheia, segue trocando de
// agrupamento, só sem lembrar dele.
const tolerantLocalStorage: StateStorage = {
    getItem: (name) => {
        try {
            return localStorage.getItem(name);
        } catch {
            return null;
        }
    },
    setItem: (name, value) => {
        try {
            localStorage.setItem(name, value);
        } catch {
            // Sem onde guardar, a escolha vale até recarregar.
        }
    },
    removeItem: (name) => {
        try {
            localStorage.removeItem(name);
        } catch {
            // Idem.
        }
    },
};

export const useSuppliersExhibitionStore = create<SuppliersExhibitionStore>()(
    persist(
        (set) => ({
            suppliersExhibitionType: OPENING_EXHIBITION_TYPE,

            setSuppliersExhibitionType: (type) => set({ suppliersExhibitionType: type }),

            updateExhibitionType: (groupBy, productType) => {
                const newType = exhibitionTypeOf(groupBy, productType);

                if (newType) {
                    set({ suppliersExhibitionType: newType });
                }
            },
        }),
        {
            name: SUPPLIERS_EXHIBITION_STORAGE_KEY,
            // localStorage é síncrono: o estado já sai hidratado da criação do store, antes da primeira busca.
            storage: createJSONStorage<PersistedExhibition>(() => tolerantLocalStorage),
            partialize: (state) => ({ suppliersExhibitionType: state.suppliersExhibitionType }),
            merge: (persisted, current) => {
                const stored = (persisted as Partial<PersistedExhibition> | undefined)?.suppliersExhibitionType;

                return {
                    ...current,
                    suppliersExhibitionType: isSuppliersExhibitionType(stored) ? stored : OPENING_EXHIBITION_TYPE,
                };
            },
            // Regrava o valor já saneado, para que ausente, desconhecido ou ilegível vire Rede no navegador.
            onRehydrateStorage: (initial) => (hydrated) => {
                const state = hydrated ?? initial;
                state.setSuppliersExhibitionType(hydrated ? hydrated.suppliersExhibitionType : OPENING_EXHIBITION_TYPE);
            },
        },
    ),
);

export const selectorSuppliersExhibitionType = (state: SuppliersExhibitionStore) => state.suppliersExhibitionType;
export const selectorCurrentGroupBy = (state: SuppliersExhibitionStore) =>
    resolveExhibitionGrouping(state.suppliersExhibitionType).groupBy;
export const selectorCurrentProductType = (state: SuppliersExhibitionStore) =>
    resolveExhibitionGrouping(state.suppliersExhibitionType).productType;
